// src/color.ts
import tinycolor from "tinycolor2";
var Color = class _Color {
  constructor(color, name = "") {
    this._tinycolor = tinycolor(color);
    this._name = name;
  }
  get name() {
    return this._name;
  }
  clone() {
    return new _Color(this.val(), this.name);
  }
  val() {
    return this._tinycolor.toRgbString();
  }
  hex() {
    return this._tinycolor.toHex();
  }
  rgb() {
    return this._tinycolor.toRgb();
  }
  hsl() {
    return this._tinycolor.toHsl();
  }
  brightness() {
    return this._tinycolor.getBrightness();
  }
  isLight() {
    return !this.isDark();
  }
  isDark() {
    return this._tinycolor.isDark();
  }
  isLighterThan(color) {
    return !this.isDarkerThan(color);
  }
  isDarkerThan(color) {
    return this.brightness() < color.brightness();
  }
  lighten(amount = 20) {
    const color = tinycolor(this.val());
    color.lighten(amount);
    return new _Color(color, this.name);
  }
  darken(amount = 20) {
    const color = tinycolor(this.val());
    color.darken(amount);
    return new _Color(color, this.name);
  }
  darkenOrLighten(amount) {
    return this.isDark() ? this.lighten(amount) : this.darken(amount);
  }
  alpha(alpha = 0.5) {
    const color = tinycolor(this.val());
    color.setAlpha(alpha);
    return new _Color(color, this.name);
  }
  analogous(results = 3) {
    return this._tinycolor.analogous(results, 15).map((tinycolor3) => {
      return new _Color(tinycolor3.toHex(), this.name);
    });
  }
  readability(color) {
    return (tinycolor.readability(this.val(), color.val()) - 1) / 20;
  }
  isReadable(color) {
    return tinycolor.isReadable(this.val(), color.val());
  }
  mostReadable(colors = []) {
    const textColors = colors.filter((color) => !!color).map((color) => color.val());
    if (textColors.length) {
      const chosenColor = tinycolor.mostReadable(this.val(), textColors);
      if (chosenColor) {
        return new _Color(chosenColor.toHex());
      }
    }
    return new _Color(
      tinycolor.mostReadable(this._tinycolor, [
        tinycolor("#000000"),
        tinycolor("#FFFFFF")
      ]).toHex()
    );
  }
  static mix(colors) {
    if (!colors || !colors.length) {
      throw new Error("At least one color is required");
    }
    const { r, g, b, a } = colors.map((c) => c.rgb()).reduce(
      (res, { r: r2, g: g2, b: b2, a: a2 }) => ({
        r: res.r + r2,
        g: res.g + g2,
        b: res.b + b2,
        a: res.a + a2
      }),
      { r: 0, g: 0, b: 0, a: 0 }
    );
    const toAvg = (x) => Math.floor(x / colors.length);
    const mixedRGB = {
      r: toAvg(r),
      g: toAvg(g),
      b: toAvg(b),
      a: toAvg(a)
    };
    return new _Color(mixedRGB);
  }
};

// src/color-set.ts
import tinycolor2 from "tinycolor2";
var _ColorSet = class _ColorSet {
  constructor(colorSet) {
    this.DEFAULT_TEXT_COLOR = "#333";
    this.DEFAULT_BACK_COLOR = "#FFF";
    this.DEFAULT_HIGHLIGHT_COLOR = "#333";
    this._colorSet = {
      "text": this.DEFAULT_TEXT_COLOR,
      "back": this.DEFAULT_BACK_COLOR,
      "highlight": this.DEFAULT_HIGHLIGHT_COLOR
    };
    if (colorSet) this._colorSet = colorSet;
  }
  get textColor() {
    var _a;
    return (_a = this._colorSet["text"]) != null ? _a : this.DEFAULT_TEXT_COLOR;
  }
  get backColor() {
    var _a;
    return (_a = this._colorSet["back"]) != null ? _a : this.DEFAULT_BACK_COLOR;
  }
  get highlightColor() {
    var _a;
    return (_a = this._colorSet["highlight"]) != null ? _a : this.DEFAULT_HIGHLIGHT_COLOR;
  }
  getColor(key, defaultKey = "highlight") {
    var _a, _b;
    const isKeyHexColor = tinycolor2(key != null ? key : "").isValid() && key.startsWith("#", 0);
    return (_b = (_a = key === "none" ? "transparent" : isKeyHexColor ? key : !!key ? this._colorSet[key] : this._colorSet[defaultKey]) != null ? _a : this._colorSet[defaultKey]) != null ? _b : this.DEFAULT_HIGHLIGHT_COLOR;
  }
  static getMainColors(colorSet) {
    const sets = Array.isArray(colorSet) ? colorSet : [colorSet];
    const colors = sets.map((set) => {
      const cs = new _ColorSet(set);
      return [cs.backColor, cs.textColor, cs.highlightColor];
    }).flat(2);
    return [...new Set(colors)];
  }
  static getColorSet(colorSets, index) {
    if (!colorSets || !colorSets.length) return void 0;
    const indexes = Array.isArray(index) ? index : [index];
    for (const i of indexes) {
      if (i === null || isNaN(+i)) continue;
      const colorSet = colorSets[Math.abs(+i) % colorSets.length];
      if (colorSet) return colorSet;
    }
    return colorSets[0];
  }
};
_ColorSet.getMostReadableColor = (backColor) => (foreColors) => {
  const back = typeof backColor === "string" ? new Color(backColor) : backColor;
  return back.mostReadable(
    foreColors.filter((c) => !!c).map((c) => typeof c === "string" ? new Color(c) : c)
  ).val();
};
var ColorSet = _ColorSet;
export {
  Color,
  ColorSet
};
